import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiX,
  FiArrowLeft,
  FiCalendar,
  FiUser,
  FiFileText,
  FiDownload,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiRefreshCw
} from "react-icons/fi";
import { MdOutlineArchive } from "react-icons/md";
import { Document, Page, pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useLanguage } from "../../contexts/LanguageContext";
import Portal from "../../components/Portal";
import "../AdminPage.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// ---------------- Archive Modal ----------------
function ArchiveModal({ isOpen, onClose, onConfirm, resource, isArchiving }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling - modal will stay open
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Archive Resource</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#fee2e2", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <MdOutlineArchive size={32} color="#dc2626" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to archive this resource?</p>
          
          {resource && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "12px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>{resource.title}</div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Category: {resource.category}
              </div>
            </div>
          )}
          
          <div style={{ 
            marginTop: "16px", 
            padding: "12px", 
            background: "#fef3c7", 
            borderRadius: "8px", 
            border: "1px solid #f59e0b"
          }}>
            <div style={{ fontSize: "14px", color: "#92400e", fontWeight: "600", textAlign: 'left' }}>
              This will:
            </div>
            <ul style={{ 
              margin: "8px 0 0 0", 
              paddingLeft: "20px", 
              fontSize: "14px", 
              color: "#92400e",
              textAlign: 'left'
            }}>
              <li>Remove resource from peer resources</li>
              <li>Resource will no longer be visible to peer educators</li>
              <li>Status will change to "Archived"</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-actions">
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isArchiving}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm archive-btn ${isArchiving ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isArchiving}
            style={{
              backgroundColor: "#dc3545",
              borderColor: "#dc3545",
              color: "white"
            }}
          >
            {isArchiving ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                <MdOutlineArchive style={{ marginRight: '4px' }} />
                Archive
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Restore Modal ----------------
function RestoreModal({ isOpen, onClose, onConfirm, resource, isRestoring }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling - modal will stay open
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Restore Resource</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#d1fae5", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiRefreshCw size={32} color="#059669" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to restore this resource?</p>
          
          {resource && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "12px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>{resource.title}</div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Category: {resource.category}
              </div>
            </div>
          )}
          
          <div style={{ 
            marginTop: "16px", 
            padding: "12px", 
            background: "#d1fae5", 
            borderRadius: "8px", 
            border: "1px solid #10b981"
          }}>
            <div style={{ fontSize: "14px", color: "#065f46", fontWeight: "600", textAlign: 'left' }}>
              This will:
            </div>
            <ul style={{ 
              margin: "8px 0 0 0", 
              paddingLeft: "20px", 
              fontSize: "14px", 
              color: "#065f46",
              textAlign: 'left'
            }}>
              <li>Add resource back to peer resources</li>
              <li>Resource will be visible to peer educators again</li>
              <li>Status will change to "Active"</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-actions">
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isRestoring}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm restore-btn ${isRestoring ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isRestoring}
            style={{
              backgroundColor: "#28a745",
              borderColor: "#28a745",
              color: "white"
            }}
          >
            {isRestoring ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                <FiRefreshCw style={{ marginRight: '4px' }} />
                Restore
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function AdminPeerResourceReview() {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resourceFileUrl, setResourceFileUrl] = useState(null);
  const [resourceFileLoading, setResourceFileLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [activeTab, setActiveTab] = useState('submission');
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    fetchResource();
  }, [resourceId]);

  const fetchResource = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "resources", resourceId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setResource(data);
        await loadResourceFile(data);
      } else {
        setError("Resource not found");
      }
    } catch (err) {
      setError("Failed to load resource. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadResourceFile = async (resourceData) => {
    try {
      setResourceFileLoading(true);
      setResourceFileUrl(null);
      setPageNumber(1);
      setNumPages(null);

      if (resourceData.fileUrl) {
        setResourceFileUrl(resourceData.fileUrl);
      }
    } catch (error) {
      // Error handling for file loading
    } finally {
      setResourceFileLoading(false);
    }
  };

  const handleArchive = () => {
    setShowArchiveModal(true);
  };

  const confirmArchive = async () => {
    try {
      setIsArchiving(true);
      await updateDoc(doc(db, "resources", resourceId), {
        status: "Archived",
        archivedAt: new Date().toISOString(),
        archivedBy: "Admin"
      });
      setShowArchiveModal(false);
      navigate("/admin-management?tab=peer-resources");
    } catch (error) {
      alert("Failed to archive resource. Please try again.");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRestore = () => {
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    try {
      setIsRestoring(true);
      await updateDoc(doc(db, "resources", resourceId), {
        status: "Active",
        restoredAt: new Date().toISOString(),
        restoredBy: "Admin"
      });
      setShowRestoreModal(false);
      navigate("/admin-management?tab=peer-resources");
    } catch (error) {
      alert("Failed to restore resource. Please try again.");
    } finally {
      setIsRestoring(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";

    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return (
      <>
        {dateStr}<br />{timeStr}
      </>
    );
  };

  const nextPage = () => {
    setPageNumber((prevPageNumber) =>
      prevPageNumber + 1 > numPages ? numPages : prevPageNumber + 1
    );
  };

  const prevPage = () => {
    setPageNumber((prevPageNumber) =>
      prevPageNumber - 1 < 1 ? 1 : prevPageNumber - 1
    );
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const isArchived = resource?.status === "Archived";
  const isActive = !isArchived;

  if (error || (!loading && !resource)) {
    return (
      <div className="content-review-page">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error || "Resource not found"}</p>
          <button className="back-btn" onClick={() => navigate("/admin-management?tab=peer-resources")}>
            <FiArrowLeft /> Back to Peer Resources
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .content-review-header::before {
          display: none !important;
        }
        .sidebar-card::before,
        .sidebar-card::after {
          display: none !important;
        }
        .admin-aura-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .admin-aura-skeleton-text {
          height: 16px;
          margin-bottom: 8px;
        }
        .admin-aura-skeleton-title {
          height: 32px;
          width: 60%;
          margin-bottom: 16px;
        }
        .admin-aura-skeleton-input {
          height: 40px;
          width: 100%;
        }
      `}</style>
      <div className="content-review-page">
        <div className="content-review-header" style={{ 
          background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
          padding: '20px 32px',
          borderBottom: 'none',
          borderRadius: '0'
        }}>
          <div className="header-left">
            <button className="back-btn" 
              onClick={() => navigate("/admin-management?tab=peer-resources")}
              style={{ 
                background: 'rgba(255,255,255,0.12)', 
                color: '#fff', 
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.18)'
              }}
            >
              <FiArrowLeft />
            </button>
            <div className="header-title">
              <h1 style={{ color: '#fff' }}>Resource Review</h1>
              <div className="submission-meta">
                <span className="submission-id-full" style={{ color: '#cbd5e1' }}>Resource ID: {resourceId || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="content-review-body" style={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
          padding: '24px'
        }}>
          <div className="content-review-main">
            {/* Tabs */}
            <div style={{ 
              display: 'flex', 
              background: '#fff',
              borderRadius: '16px 16px 0 0',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              borderBottom: 'none'
            }}>
              <button 
                onClick={() => setActiveTab('submission')}
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  border: 'none',
                  background: activeTab === 'submission' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'submission' ? '#fff' : '#475569',
                  fontSize: '14px',
                  fontWeight: activeTab === 'submission' ? 700 : 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <FiUser /> Submission Information
              </button>
              <button 
                onClick={() => setActiveTab('file')}
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  border: 'none',
                  background: activeTab === 'file' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'file' ? '#fff' : '#475569',
                  fontSize: '14px',
                  fontWeight: activeTab === 'file' ? 700 : 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <FiFileText /> File Preview
              </button>
            </div>

            {/* Tab Content */}
            <div style={{ 
              background: '#fff',
              borderRadius: '0 0 16px 16px',
              padding: '28px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              minHeight: '500px'
            }}>
              {activeTab === 'submission' && (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    marginBottom: '24px',
                    paddingBottom: '16px',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    <FiUser style={{ color: '#3f62b5', fontSize: '20px' }} />
                    <h2 style={{ 
                      margin: 0, 
                      fontSize: '18px', 
                      color: '#1e293b', 
                      fontWeight: 700 
                    }}>Submission Information</h2>
                  </div>
                  
                  <div className="info-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '20px' 
                  }}>
                    {loading ? (
                      <>
                        <div className="info-item">
                          <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                            <FiCalendar style={{ marginRight: '6px', color: '#3f62b5' }} /> Created Date
                          </label>
                          <div className="info-value" style={{ 
                            background: '#f8fafc', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '10px',
                            padding: '12px 16px',
                            fontSize: '15px',
                            fontWeight: 600
                          }}>
                            <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100%', margin: 0 }} />
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Category</label>
                          <div className="info-value" style={{ 
                            background: '#f8fafc', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '10px',
                            padding: '12px 16px',
                            fontSize: '15px',
                            fontWeight: 600
                          }}>
                            <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100%', margin: 0 }} />
                          </div>
                        </div>

                      </>
                    ) : (
                      <>
                        <div className="info-item">
                          <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                            <FiCalendar style={{ marginRight: '6px', color: '#3f62b5' }} /> Created Date
                          </label>
                          <div className="info-value" style={{ 
                            background: '#f8fafc', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '10px',
                            padding: '12px 16px',
                            fontSize: '15px',
                            fontWeight: 600
                          }}>{formatDate(resource.createdAt)}</div>
                        </div>
                        
                        <div className="info-item">
                          <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Category</label>
                          <div className="info-value" style={{ 
                            background: '#f8fafc', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '10px',
                            padding: '12px 16px',
                            fontSize: '15px',
                            fontWeight: 600
                          }}>{resource.category}</div>
                        </div>

                        {resource.status === "Approved Requests" && (
                          <div className="info-item">
                            <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                              <FiCalendar style={{ marginRight: '6px', color: '#3f62b5' }} /> Approved Date
                            </label>
                            <div className="info-value" style={{ 
                              background: '#f8fafc', 
                              border: '1px solid #e2e8f0', 
                              borderRadius: '10px',
                              padding: '12px 16px',
                              fontSize: '15px',
                              fontWeight: 600
                            }}>{formatDate(resource.restoredAt || resource.approvedAt)}</div>
                          </div>
                        )}
                        
                        {resource.status === "Rejected" && (
                          <>
                            <div className="info-item">
                              <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                                <FiCalendar style={{ marginRight: '6px', color: '#3f62b5' }} /> Rejected Date
                              </label>
                              <div className="info-value" style={{ 
                                background: '#f8fafc', 
                                border: '1px solid #e2e8f0', 
                                borderRadius: '10px',
                                padding: '12px 16px',
                                fontSize: '15px',
                                fontWeight: 600
                              }}>{formatDate(resource.rejectedAt)}</div>
                            </div>
                            
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                              <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                                <FiX style={{ marginRight: '6px', color: '#dc2626' }} /> Rejection Reason
                              </label>
                              <div className="info-value" style={{ 
                                background: '#fef2f2', 
                                border: '1px solid #fecaca', 
                                borderRadius: '10px',
                                padding: '14px 18px',
                                fontSize: '15px',
                                lineHeight: '1.6'
                              }}>
                                {resource.rejectReason || 'No rejection reason found'}
                              </div>
                            </div>
                          </>
                        )}
                        
                        {resource.status === "Archived" && (
                          <div className="info-item">
                            <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                              <FiCalendar style={{ marginRight: '6px', color: '#3f62b5' }} /> Archived Date
                            </label>
                            <div className="info-value" style={{ 
                              background: '#f8fafc', 
                              border: '1px solid #e2e8f0', 
                              borderRadius: '10px',
                              padding: '12px 16px',
                              fontSize: '15px',
                              fontWeight: 600
                            }}>{formatDate(resource.archivedAt)}</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'file' && (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    marginBottom: '24px',
                    paddingBottom: '16px',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    <FiFileText style={{ color: '#3f62b5', fontSize: '20px' }} />
                    <h2 style={{ 
                      margin: 0, 
                      fontSize: '18px', 
                      color: '#1e293b', 
                      fontWeight: 700 
                    }}>File Preview</h2>
                  </div>
                  
                  <div className="article-content">
                    <div className="article-header" style={{ marginBottom: '24px' }}>
                      {loading ? (
                        <div className="admin-aura-skeleton admin-aura-skeleton-title" style={{ width: '70%' }} />
                      ) : (
                        <h1 className="article-title" style={{ 
                          fontSize: '28px', 
                          color: '#1e293b', 
                          margin: 0,
                          fontWeight: 800
                        }}>{resource.title}</h1>
                      )}
                    </div>

                    <div style={{ 
                      minHeight: '500px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '12px', 
                      background: '#fff',
                      overflow: 'hidden'
                    }}>
                      {resourceFileLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '500px' }}>
                          Loading file...
                        </div>
                      ) : !resourceFileUrl ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '500px' }}>
                          No file available for preview
                        </div>
                      ) : (
                        <>
                          {/* PDF Viewer */}
                          {resource.type.toLowerCase() === 'pdf' && (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '500px' }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                gap: '16px', 
                                padding: '14px', 
                                background: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                                flexWrap: 'wrap'
                              }}>
                                <button 
                                  onClick={prevPage}
                                  disabled={pageNumber <= 1}
                                  style={{
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                                    fontWeight: 600,
                                    opacity: pageNumber <= 1 ? 0.6 : 1
                                  }}
                                >
                                  Previous
                                </button>
                                <span style={{ 
                                  fontSize: '15px', 
                                  fontWeight: 600, 
                                  color: '#475569' 
                                }}>
                                  Page {pageNumber} of {numPages}
                                </span>
                                <button 
                                  onClick={nextPage}
                                  disabled={pageNumber >= numPages}
                                  style={{
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer',
                                    fontWeight: 600,
                                    opacity: pageNumber >= numPages ? 0.6 : 1
                                  }}
                                >
                                  Next
                                </button>
                                <a 
                                  href={resourceFileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #047857 0%, #10b981 100%)',
                                    color: 'white',
                                    borderRadius: '10px',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                  }}
                                >
                                  <FiDownload /> Download
                                </a>
                              </div>
                              <div style={{ 
                                flex: 1, 
                                overflow: 'auto', 
                                display: 'flex', 
                                justifyContent: 'center',
                                padding: '20px',
                                background: '#f1f5f9'
                              }}>
                                <Document 
                                  file={resourceFileUrl} 
                                  onLoadSuccess={onDocumentLoadSuccess}
                                  loading={<div>Loading PDF...</div>}
                                  error={<div>Failed to load PDF</div>}
                                >
                                  <Page 
                                    pageNumber={pageNumber} 
                                    width={650}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    style={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                  />
                                </Document>
                              </div>
                            </div>
                          )}

                          {/* Image Viewer */}
                          {resource.type.toLowerCase() === 'image' && (
                            <div style={{ 
                              height: '100%', 
                              minHeight: '500px',
                              display: 'flex', 
                              flexDirection: 'column'
                            }}>
                              <div style={{ 
                                padding: '14px', 
                                background: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'flex-end'
                              }}>
                                <a 
                                  href={resourceFileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #047857 0%, #10b981 100%)',
                                    color: 'white',
                                    borderRadius: '10px',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                  }}
                                >
                                  <FiDownload /> Download
                                </a>
                              </div>
                              <div style={{ 
                                flex: 1, 
                                overflow: 'auto', 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '20px',
                                background: '#f1f5f9'
                              }}>
                                <img 
                                  src={resourceFileUrl} 
                                  alt={resource.title} 
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '600px', 
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* DOC/DOCX Viewer - Download Only */}
                          {(resource.type.toLowerCase() === 'doc' || resource.type.toLowerCase() === 'document' || resource.type.toLowerCase() === 'docx') && (
                            <div style={{ 
                              height: '100%', 
                              minHeight: '500px',
                              display: 'flex', 
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              padding: '32px',
                              background: '#f8fafc'
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <FiFileText size={56} style={{ color: '#64748b', marginBottom: '20px' }} />
                                <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '16px' }}>Document preview not available</p>
                                <a 
                                  href={resourceFileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '14px 28px',
                                    background: 'linear-gradient(135deg, #047857 0%, #10b981 100%)',
                                    color: 'white',
                                    borderRadius: '10px',
                                    textDecoration: 'none',
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                  }}
                                >
                                  <FiDownload /> Download Document
                                </a>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Resource Details */}
          <aside className="content-review-sidebar">
            <div className="sidebar-card" style={{ 
              background: '#fff',
              borderRadius: '16px',
              padding: '0',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                padding: '18px 24px'
              }}>
                <h3 style={{ 
                  color: '#fff !important',
                  WebkitTextFillColor: '#fff',
                  margin: 0, 
                  fontSize: '16px', 
                  fontWeight: 700,
                  textAlign: 'center'
                }}>Resource Details</h3>
              </div>
              <div className="detail-list" style={{ padding: '24px', background: '#fff' }}>
                {loading ? (
                  <>
                    <div className="detail-item" style={{ 
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '14px 18px',
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '14px' }}>File Name:</span>
                      <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '120px', margin: 0 }} />
                    </div>
                    
                    <div className="detail-item" style={{ 
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '10px',
                      padding: '14px 18px',
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '14px' }}>File Type:</span>
                      <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '80px', margin: 0 }} />
                    </div>

                    <div className="detail-item" style={{ 
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '14px 18px',
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '14px' }}>File Size:</span>
                      <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100px', margin: 0 }} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="detail-item" style={{ 
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '14px 18px',
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '14px' }}>File Name:</span>
                      <span className="detail-value" style={{ color: '#1e293b', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>{resource.file}</span>
                    </div>
                
                    <div className="detail-item" style={{ 
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '10px',
                      padding: '14px 18px',
                      marginBottom: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      textAlign: 'center'
                    }}>
                      <span className="detail-label" style={{ color: '#166534', fontWeight: 600, fontSize: '13px' }}>
                        {isArchived ? (
                          <FiAlertCircle style={{ display: 'inline', marginRight: '6px', color: '#dc2626' }} />
                        ) : (
                          <FiCheckCircle style={{ display: 'inline', marginRight: '6px', color: '#059669' }} />
                        )}
                        {isArchived ? 'Archived' : 'Active'}
                      </span>
                      <span style={{ color: '#475569', fontSize: '13px' }}>
                        This resource is {isArchived ? 'currently archived' : 'active and published'}.
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '10px', display: 'block' }}>Action:</span>
                      <span className="detail-value" style={{ display: 'block' }}>
                        {isActive ? (
                          <button 
                            className="archive-btn"
                            onClick={handleArchive}
                            style={{
                              width: '100%',
                              borderRadius: '10px',
                              padding: '12px 20px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                              color: 'white',
                              cursor: 'pointer',
                              fontWeight: 700,
                              fontSize: '14px',
                              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                            }}
                          >
                            Archive
                          </button>
                    ) : (
                      <button 
                        className="restore-btn"
                        onClick={handleRestore}
                        style={{
                          width: '100%',
                          borderRadius: '10px',
                          padding: '12px 20px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #047857 0%, #10b981 100%)',
                          color: 'white',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: '14px',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                        }}
                      >
                        Restore
                      </button>
                    )}
                  </span>
                </div>
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Archive Modal */}
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={confirmArchive}
        resource={resource}
        isArchiving={isArchiving}
      />

      {/* Restore Modal */}
      <RestoreModal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        onConfirm={confirmRestore}
        resource={resource}
        isRestoring={isRestoring}
      />
    </div>
  );
}

export default AdminPeerResourceReview;
